/* ----------------------------------------------------------------------------
 * AuraMark — the popup-header Aura mark. This is the EXACT BookSesh Aura orb
 * (booksesh.com/partners onboarding `.ob-aura`), copied VERBATIM from
 * booksesh-onboarding/assets/onboarding-entry.css lines 656–716 (extract:
 * reports-findings/architecture/booksesh-aura-ui-extract-2026-07-18.md). Only
 * the class names are namespaced (.ob-aura -> .bc-aura) so they cannot collide
 * with app CSS; every gradient stop, coral-glow rgba, keyframe value and size
 * is byte-faithful. The mark is a single empty <div> — no SVG/PNG/font.
 *
 * Two-temperature signature (do NOT flatten): cool indigo body
 * (#6f7cff -> #4b35e8, light source at 35%/30%) + warm coral halo
 * (rgba(255,107,107,…)) via a 3-layer box-shadow + a blurred ::before halo.
 * The IDLE orb stays byte-faithful; only the three ACTIVE voice states recolour
 * (state CSS lives in ./state-visuals/stateVisualStyle so it is unit-testable
 * and higher-specificity than this base — see AURA_STATE_ORB_CSS):
 *   idle -> calm 3.8s `bc-auraBreath` (BookSesh signature)
 *   listening -> teal glow, quicker scale pulse
 *   thinking  -> violet glow, slower thoughtful pulse
 *   speaking  -> amber centre + coral/magenta glow, energetic pulse
 * A one-shot `bc-aura--wave` "hello" plays on mount from idle.
 * prefers-reduced-motion is honoured for every state (motion off, static glow).
 *
 * This is the isolated swap slot — VoiceCopilot references ONLY this component,
 * so the mark can evolve without touching popup layout.
 * -------------------------------------------------------------------------- */
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import type { AuraOrbState } from './AuraOrb.tsx';
import { orbStateModifier, AURA_STATE_ORB_CSS } from './state-visuals/stateVisualStyle.ts';

export type AuraMarkState = AuraOrbState;

const AURA_MARK_STYLE_ID = 'bc-aura-mark-css';
const AURA_MARK_BASE_CSS = `
.bc-aura {
    position: relative;
    border-radius: 999px;
    background: radial-gradient(circle at 35% 30%, #6f7cff 0%, #4b35e8 70%);
    flex-shrink: 0;
    will-change: transform;
    box-shadow:
        0 0 22px rgba(255, 107, 107, 0.18),
        0 0 54px rgba(255, 107, 107, 0.10),
        0 0 96px rgba(255, 107, 107, 0.06);
    animation: bc-auraBreath 3.8s ease-in-out infinite;
}
.bc-aura::before {
    content: "";
    position: absolute;
    inset: -34px;
    border-radius: 999px;
    background: radial-gradient(circle,
        rgba(255, 107, 107, 0.16) 0%,
        rgba(255, 107, 107, 0.09) 35%,
        rgba(255, 107, 107, 0.035) 62%,
        rgba(255, 107, 107, 0) 78%
    );
    z-index: -1;
    filter: blur(8px);
}
.bc-aura--sm  { width: 40px;  height: 40px; }
.bc-aura--md  { width: 56px;  height: 56px; }
.bc-aura--lg  { width: 80px;  height: 80px; }

@keyframes bc-auraBreath {
    0%, 100% {
        transform: scale(1);
        box-shadow:
            0 0 18px rgba(255, 107, 107, 0.14),
            0 0 44px rgba(255, 107, 107, 0.08),
            0 0 84px rgba(255, 107, 107, 0.045);
    }
    50% {
        transform: scale(1.08);
        box-shadow:
            0 0 26px rgba(255, 107, 107, 0.20),
            0 0 64px rgba(255, 107, 107, 0.12),
            0 0 108px rgba(255, 107, 107, 0.065);
    }
}
@keyframes bc-aura-wave {
    0%   { transform: rotate(0deg) scale(1); }
    20%  { transform: rotate(-15deg) scale(1.05); }
    40%  { transform: rotate(15deg) scale(1.05); }
    60%  { transform: rotate(-8deg) scale(1.02); }
    80%  { transform: rotate(4deg) scale(1.01); }
    100% { transform: rotate(0deg) scale(1); }
}
.bc-aura--wave { animation: bc-aura-wave 0.8s ease-out forwards, bc-auraBreath 3.8s ease-in-out 0.8s infinite; }

@media (prefers-reduced-motion: reduce) {
    .bc-aura { animation: none; box-shadow: 0 0 18px rgba(255,107,107,0.12); }
    .bc-aura::before { display: none; }
}
`;

// Base (verbatim BookSesh) + the state recolour layer (idle untouched).
const AURA_MARK_CSS = AURA_MARK_BASE_CSS + AURA_STATE_ORB_CSS;

function ensureStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(AURA_MARK_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = AURA_MARK_STYLE_ID;
  el.textContent = AURA_MARK_CSS;
  document.head.appendChild(el);
}

export interface AuraMarkProps {
  /** reactive state — drives the glow colour + pulse (idle/listening/thinking/speaking). */
  state?: AuraMarkState;
  /** square footprint in px (default 40 = BookSesh --sm; popup spec ~36–40). */
  sizePx?: number;
  className?: string;
  /** accessible label (header names Aura in text, so decorative by default). */
  label?: string;
}

export function AuraMark({ state = 'idle', sizePx = 40, className, label }: AuraMarkProps) {
  ensureStyles();
  // One-shot "wave hello" on mount (from rest), then it settles into the breath.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    ensureStyles();
    const t = setTimeout(() => setEntered(true), 850);
    return () => clearTimeout(t);
  }, []);

  const stateMod = orbStateModifier(state);
  // Wave-hello only plays from the calm idle mount; an active state takes over.
  const useWave = !entered && state === 'idle';
  const cls = cn('bc-aura', useWave ? 'bc-aura--wave' : stateMod, className);

  return (
    <span
      style={{ display: 'inline-flex', flex: '0 0 auto', width: sizePx, height: sizePx }}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <div className={cls} style={{ width: sizePx, height: sizePx }} />
    </span>
  );
}
